import React from 'react';
import { useQuery } from 'react-query';
import api from '../services/api.ts';
import { Link } from 'react-router-dom';
import {
  ArrowTrendingUpIcon,
  BuildingLibraryIcon,
  TruckIcon,
  BanknotesIcon,
  PaperAirplaneIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import BoatIcon from '../components/icons/BoatIcon.tsx';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import companiesApi from '../services/companies.ts';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

// Types
interface DashboardData {
  totalAssets: number;
  totalVehicles: number;
  totalVessels: number;
  totalAircrafts: number;
  totalDebts: number;
  totalReceivables: number;
  totalInvestments: number;
  netWorth: number;
  assetsByType: { type: string; value: number }[];
  recentTransactions: {
    id: string;
    description: string;
    amount: number;
    date: string;
    type: 'asset' | 'vehicle' | 'vessel' | 'aircraft' | 'debt' | 'receivable' | 'investment';
  }[];
  monthlyEvolution: { month: string; netWorth: number }[];
}

const Dashboard: React.FC = () => {
  const { data, isLoading, error } = useQuery<DashboardData>(
    'dashboardData',
    async () => {
      const response = await api.get('/dashboard');
      return response.data;
    },
    {
      staleTime: 1000 * 60 * 5,
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    }
  );

  const { data: companies, isLoading: companiesLoading } = useQuery(
    'companiesSummary',
    companiesApi.summary,
    { staleTime: 1000 * 60 * 5, refetchOnMount: 'always', refetchOnWindowFocus: true, refetchOnReconnect: true }
  );

  const { data: rentals } = useQuery<any[]>(
    'rentalsDashboard',
    async () => {
      const res = await api.get('/rentals');
      return res.data;
    },
    { staleTime: 1000 * 60 * 5 }
  );
  const activeRentals = (rentals || []).filter(r => r.status === 'active').length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center text-red-500">
        Erro ao carregar dados do dashboard. Por favor, tente novamente.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <ArrowTrendingUpIcon className="h-8 w-8 text-primary-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600">Visão geral do seu patrimônio</p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4">
          <SummaryCard
            title="Patrimônio Líquido"
            value={`R$ ${data.netWorth.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            icon={<ArrowTrendingUpIcon className="h-8 w-8 text-green-600" />}
            gradient="from-green-500 to-emerald-500"
            centerY
          />
        </div>
        <div className="lg:col-span-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <BanknotesIcon className="h-8 w-8 text-teal-600" />
            </div>
            <div className="ml-4 w-full">
              <p className="text-sm font-medium text-gray-600">Resumo de Itens</p>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center">
                    <BuildingLibraryIcon className="h-5 w-5 text-primary-600" />
                    <span className="ml-2 text-xs text-gray-600">Ativos</span>
                  </div>
                  <div className="mt-1 text-lg font-bold text-gray-900">{data.totalAssets}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center">
                    <TruckIcon className="h-5 w-5 text-purple-600" />
                    <span className="ml-2 text-xs text-gray-600">Veículos</span>
                  </div>
                  <div className="mt-1 text-lg font-bold text-gray-900">{data.totalVehicles}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center">
                    <BoatIcon className="h-5 w-5 text-blue-600" />
                    <span className="ml-2 text-xs text-gray-600">Embarcações</span>
                  </div>
                  <div className="mt-1 text-lg font-bold text-gray-900">{data.totalVessels}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center">
                    <PaperAirplaneIcon className="h-5 w-5 text-indigo-600" />
                    <span className="ml-2 text-xs text-gray-600">Aeronaves</span>
                  </div>
                  <div className="mt-1 text-lg font-bold text-gray-900">{data.totalAircrafts}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center">
                    <BanknotesIcon className="h-5 w-5 text-orange-600" />
                    <span className="ml-2 text-xs text-gray-600">Dívidas</span>
                  </div>
                  <div className="mt-1 text-lg font-bold text-gray-900">{data.totalDebts}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center">
                    <BanknotesIcon className="h-5 w-5 text-teal-600" />
                    <span className="ml-2 text-xs text-gray-600">Recebíveis</span>
                  </div>
                  <div className="mt-1 text-lg font-bold text-gray-900">{data.totalReceivables}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center">
                    <BanknotesIcon className="h-5 w-5 text-amber-600" />
                    <span className="ml-2 text-xs text-gray-600">Investimentos</span>
                  </div>
                  <div className="mt-1 text-lg font-bold text-gray-900">{data.totalInvestments}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center">
                    <BuildingOfficeIcon className="h-5 w-5 text-green-600" />
                    <span className="ml-2 text-xs text-gray-600">Aluguéis Ativos</span>
                  </div>
                  <div className="mt-1 text-lg font-bold text-gray-900">{activeRentals}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Asset Distribution */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Distribuição de Patrimônio</h2>
          <div className="h-64 flex items-center justify-center">
            <Doughnut
              data={{
                labels: data.assetsByType.map(item => item.type),
                datasets: [
                  {
                    data: data.assetsByType.map(item => item.value),
                    backgroundColor: data.assetsByType.map((_, idx) => {
                      const palette = ['#3B82F6','#10B981','#8B5CF6','#F59E0B','#EF4444','#14B8A6','#F97316','#6366F1'];
                      return palette[idx % palette.length];
                    }),
                    borderColor: data.assetsByType.map((_, idx) => {
                      const borderPalette = ['#2563EB','#059669','#7C3AED','#D97706','#DC2626','#0F766E','#C2410C','#4F46E5'];
                      return borderPalette[idx % borderPalette.length];
                    }),
                    borderWidth: 2,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom' as const,
                    labels: {
                      padding: 20,
                      usePointStyle: true,
                    },
                  },
                  tooltip: {
                    callbacks: {
                      label: function (context) {
                        const value = context.parsed;
                        const total = (context.dataset?.data || []).reduce(
                          (sum: number, v: number) => sum + v,
                          0
                        );
                        const percentage = total ? (value / total) * 100 : 0;
                        const formattedValue = value.toLocaleString('pt-BR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        });
                        return `${context.label}: R$ ${formattedValue} (${percentage.toFixed(2)}%)`;
                      },
                    },
                  },
                },
              }}
            />
          </div>
          <div className="mt-6 space-y-3">
            {([...data.assetsByType].sort((a, b) => b.value - a.value)).map((item) => (
              <div
                key={item.type}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center">
                  {(() => {
                    const originalIndex = data.assetsByType.findIndex(t => t.type === item.type);
                    const palette = ['#3B82F6','#10B981','#8B5CF6','#F59E0B','#EF4444','#14B8A6','#F97316','#6366F1'];
                    const color = palette[originalIndex % palette.length] || '#9CA3AF';
                    return <div className="w-4 h-4 rounded-full" style={{ backgroundColor: color }}></div>;
                  })()}
                  <span className="ml-3 text-sm font-medium text-gray-700">{item.type}</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">
                  R$ {item.value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Monthly Evolution Chart */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Evolução Mensal</h2>
          <div className="h-64">
            <Bar
              data={{
                labels: (data.monthlyEvolution && data.monthlyEvolution.length
                  ? data.monthlyEvolution.map(item => item.month)
                  : ['Jan','Fev','Mar','Abr','Mai','Jun']),
                datasets: [
                  {
                    label: 'Patrimônio',
                    data: (data.monthlyEvolution && data.monthlyEvolution.length
                      ? data.monthlyEvolution.map(item => item.netWorth)
                      : [
                        data.netWorth * 0.85,
                        data.netWorth * 0.88,
                        data.netWorth * 0.92,
                        data.netWorth * 0.95,
                        data.netWorth * 0.98,
                        data.netWorth,
                      ]),
                    backgroundColor: 'rgba(59, 130, 246, 0.5)',
                    borderColor: 'rgb(59, 130, 246)',
                    borderWidth: 2,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false,
                  },
                  tooltip: {
                    callbacks: {
                      label: function (context) {
                        const value = context.parsed.y;
                        return `Patrimônio: R$ ${value.toLocaleString('pt-BR')}`;
                      },
                    },
                  },
                },
                scales: {
                  y: {
                    beginAtZero: false,
                    ticks: {
                      callback: function (value) {
                        return `R$ ${Number(value).toLocaleString('pt-BR', { notation: 'compact' })}`;
                      },
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Transações Recentes</h2>
          <div className="space-y-4">
            {data.recentTransactions.map(transaction => (
              <Link
                key={transaction.id}
                to={
                  transaction.type === 'asset'
                    ? `/assets/${transaction.id}`
                    : transaction.type === 'vehicle'
                    ? `/vehicles/${transaction.id}`
                    : transaction.type === 'vessel'
                    ? `/vessels/${transaction.id}`
                    : transaction.type === 'aircraft'
                    ? `/aircrafts/${transaction.id}`
                    : transaction.type === 'receivable'
                    ? `/receivables/${transaction.id}`
                    : transaction.type === 'investment'
                    ? `/investments/${transaction.id}`
                    : `/debts/${transaction.id}`
                }
                className="flex items-center p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <div
                  className={`flex-shrink-0 h-12 w-12 rounded-xl flex items-center justify-center ${
                    transaction.type === 'asset'
                      ? 'bg-green-100 text-green-600'
                    : transaction.type === 'vehicle'
                      ? 'bg-primary-100 text-primary-600'
                    : transaction.type === 'vessel'
                      ? 'bg-blue-100 text-blue-600'
                    : transaction.type === 'aircraft'
                      ? 'bg-indigo-100 text-indigo-600'
                    : transaction.type === 'receivable'
                      ? 'bg-teal-100 text-teal-600'
                    : transaction.type === 'investment'
                      ? 'bg-amber-100 text-amber-600'
                    : 'bg-red-100 text-red-600'
                  }`}
                >
                  {transaction.type === 'asset' ? (
                    <BuildingLibraryIcon className="h-6 w-6" />
                  ) : transaction.type === 'vehicle' ? (
                    <TruckIcon className="h-6 w-6" />
                  ) : transaction.type === 'vessel' ? (
                    <BoatIcon className="h-6 w-6" />
                  ) : transaction.type === 'aircraft' ? (
                    <PaperAirplaneIcon className="h-6 w-6" />
                  ) : (
                    <BanknotesIcon className="h-6 w-6" />
                  )}
                </div>
                <div className="ml-4 flex-1">
                  <div className="text-sm font-semibold text-gray-900">
                    {transaction.description}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {new Date(transaction.date).toLocaleDateString('pt-BR')}
                  </div>
                </div>
                <div
                  className={`text-sm font-bold ${
                    transaction.amount >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {transaction.amount >= 0 ? '+' : ''}
                  R$ {Math.abs(transaction.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Companies Charts */}
      {companies && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Lucro por Empresa */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Lucro por Empresa</h2>
            <div className="h-64">
              <Bar
                data={{
                  labels: companies.profitByCompany.map(c => c.name),
                  datasets: [
                    {
                      label: 'Lucro Anual (R$)',
                      data: companies.profitByCompany.map(c => c.value),
                      backgroundColor: 'rgba(16, 185, 129, 0.5)',
                      borderColor: 'rgb(16, 185, 129)',
                      borderWidth: 2,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          const v = context.parsed.y;
                          return `Lucro: R$ ${Number(v).toLocaleString('pt-BR')}`;
                        },
                      },
                    },
                  },
                }}
              />
            </div>
          </div>

          {/* Valuation por Empresa */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Valuation por Empresa</h2>
            <div className="h-64">
              <Bar
                data={{
                  labels: companies.valuationByCompany.map(c => c.name),
                  datasets: [
                    {
                      label: 'Valuation (R$)',
                      data: companies.valuationByCompany.map(c => c.value),
                      backgroundColor: 'rgba(59, 130, 246, 0.5)',
                      borderColor: 'rgb(59, 130, 246)',
                      borderWidth: 2,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          const v = context.parsed.y;
                          return `Valuation: R$ ${Number(v).toLocaleString('pt-BR')}`;
                        },
                      },
                    },
                  },
                }}
              />
            </div>
          </div>

          {/* Margem por Empresa */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Margem por Empresa</h2>
            <div className="h-64 flex items-center justify-center">
              <Doughnut
                data={{
                  labels: companies.marginByCompany.map(c => c.name),
                  datasets: [
                    {
                      data: companies.marginByCompany.map(c => c.value),
                      backgroundColor: [
                        '#3B82F6',
                        '#10B981',
                        '#8B5CF6',
                        '#F59E0B',
                        '#EF4444',
                        '#14B8A6',
                      ],
                      borderColor: [
                        '#2563EB',
                        '#059669',
                        '#7C3AED',
                        '#D97706',
                        '#DC2626',
                        '#0F766E',
                      ],
                      borderWidth: 2,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          const v = context.parsed;
                          return `${context.label}: ${Number(v).toFixed(2)}%`;
                        },
                      },
                    },
                    legend: { position: 'bottom' as const },
                  },
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Additional Company Charts */}
      {companies && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Receita por Empresa */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Receita por Empresa</h2>
            <div className="h-64">
              <Bar
                data={{
                  labels: companies.revenueByCompany.map((c: any) => c.name),
                  datasets: [
                    {
                      label: 'Receita Anual (R$)',
                      data: companies.revenueByCompany.map((c: any) => c.value),
                      backgroundColor: 'rgba(245, 158, 11, 0.5)',
                      borderColor: 'rgb(245, 158, 11)',
                      borderWidth: 2,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          const v = context.parsed.y;
                          return `Receita: R$ ${Number(v).toLocaleString('pt-BR')}`;
                        },
                      },
                    },
                  },
                }}
              />
            </div>
          </div>

          {/* Lucro x Receita por Empresa */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Lucro x Receita por Empresa
            </h2>
            <div className="h-64">
              {(() => {
                const labels = companies.revenueByCompany.map((c: any) => c.name);
                const revenueMap = new Map<string, number>();
                companies.revenueByCompany.forEach((c: any) => revenueMap.set(c.name, c.value));
                const profitMap = new Map<string, number>();
                companies.profitByCompany.forEach((c: any) => profitMap.set(c.name, c.value));
                const revenueData = labels.map((name: string) => revenueMap.get(name) || 0);
                const profitData = labels.map((name: string) => profitMap.get(name) || 0);
                return (
                  <Bar
                    data={{
                      labels,
                      datasets: [
                        {
                          label: 'Receita (R$)',
                          data: revenueData,
                          backgroundColor: 'rgba(245, 158, 11, 0.5)',
                          borderColor: 'rgb(245, 158, 11)',
                          borderWidth: 2,
                        },
                        {
                          label: 'Lucro (R$)',
                          data: profitData,
                          backgroundColor: 'rgba(16, 185, 129, 0.5)',
                          borderColor: 'rgb(16, 185, 129)',
                          borderWidth: 2,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        tooltip: {
                          callbacks: {
                            label: function (context) {
                              const v = context.parsed.y;
                              return `${context.dataset.label}: R$ ${Number(v).toLocaleString('pt-BR')}`;
                            },
                          },
                        },
                        legend: { position: 'bottom' as const },
                      },
                      scales: {
                        y: { beginAtZero: true },
                      },
                    }}
                  />
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface SummaryCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  change?: string;
  changeType?: 'positive' | 'negative' | 'neutral';
  gradient?: string;
  centerY?: boolean;
}

const SummaryCard: React.FC<SummaryCardProps> = ({
  title,
  value,
  icon,
  change,
  changeType,
  gradient = 'from-gray-500 to-gray-600',
  centerY = false,
}) => {
  const changeColor =
    changeType === 'positive'
      ? 'text-green-600'
      : changeType === 'negative'
        ? 'text-red-600'
        : 'text-gray-600';

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className={`flex items-center ${centerY ? 'min-h-24' : ''}`}> 
        <div className="flex-shrink-0">{icon}</div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {change && changeType && (
            <div className="flex items-center mt-1">
              <span className={`text-sm font-medium ${changeColor}`}>{change}</span>
              <span className="text-xs text-gray-500 ml-1">vs mês anterior</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
